import { EVENT_CATALOG, ENTITY_CATALOG } from './catalog/index.js';
import { toCanonicalEventName, parseEventName, toCamelCase } from './naming.js';

/**
 * Returns true if the canonical (or legacy) event name exists in the catalog.
 */
export function isKnownEvent(name) {
    if (!name) return false;
    if (EVENT_CATALOG[name]) return true;
    return !!EVENT_CATALOG[toCanonicalEventName(name)];
}

/**
 * Returns the catalog definition for an event name (canonical or legacy).
 */
export function getEventDef(name) {
    if (!name) return null;
    return EVENT_CATALOG[name] || EVENT_CATALOG[toCanonicalEventName(name)] || null;
}

export function getEventNames() {
    return Object.keys(EVENT_CATALOG);
}

export function getEntityNames() {
    return Object.keys(ENTITY_CATALOG);
}

export function getEventsForEntity(entityRaw) {
    const entity = toCamelCase(entityRaw);
    const node = ENTITY_CATALOG[entity];
    if (!node) return [];
    return Object.values(node.events).map((def) => def.name);
}

/**
 * Returns true if the event is part of an entity's CRUD lifecycle.
 * Workflow trigger bridge uses this.
 */
export function isLifecycleEvent(name) {
    const def = getEventDef(name);
    return !!def?.lifecycle;
}

/**
 * Backward-compatible shim of the historical `ENTITY_EVENT_REGISTRY`.
 * Returns `{ [entity]: ['created', 'updated', 'deleted', ...] }` containing
 * **only** the lifecycle verbs for each entity. Used by the workflow trigger
 * bridge and template validator while migration to catalog completes.
 *
 * NOTE: keys are camelCase (canonical). Old code expecting PascalCase should
 * route through `parseEventName` / `toCamelCase` first.
 */
export function getLifecycleRegistry() {
    const out = Object.create(null);
    for (const [entity, node] of Object.entries(ENTITY_CATALOG)) {
        const verbs = [];
        for (const def of Object.values(node.events)) {
            if (!def.lifecycle) continue;
            // Only top-level verbs (no sub-resource lifecycle yet).
            if (def.subResource) continue;
            verbs.push(def.verb);
        }
        if (verbs.length) out[entity] = verbs;
    }
    return out;
}

/**
 * UI helper: groups catalog events by entity for the automation event picker.
 * Replaces the static `automation-events.js` list.
 */
export function getAutomationOptions({ includeDeprecated = false } = {}) {
    const groups = [];
    for (const [entity, node] of Object.entries(ENTITY_CATALOG)) {
        const items = [];
        for (const def of Object.values(node.events)) {
            if (def.deprecated && !includeDeprecated) continue;
            items.push({ value: def.name, label: def.name, scope: def.scope, lifecycle: def.lifecycle });
        }
        if (items.length === 0) continue;
        groups.push({ entity, label: entity, options: items });
    }
    return groups;
}

/**
 * Convenience: wildcard-aware match.
 * Accepts patterns like `risk.*` or `*.created`.
 */
export function matchEventPattern(pattern, name) {
    const canonicalPattern = toCanonicalEventName(pattern);
    const canonicalName = toCanonicalEventName(name);
    if (canonicalPattern === '*' || canonicalPattern === canonicalName) return true;
    if (!canonicalPattern.includes('*')) return false;
    const regex = new RegExp('^' + canonicalPattern.replace(/\./g, '\\.').replace(/\*/g, '[^.]+') + '$');
    return regex.test(canonicalName);
}

/**
 * Re-export for ergonomic imports.
 */
export { EVENT_CATALOG, ENTITY_CATALOG, parseEventName, toCanonicalEventName, toCamelCase };
